import React from 'react';
import {
  Button,
  themeUtils as tu,
  useModal,
  Modal,
  ModalContent,
  Alert,
  notification,
} from '@zen-common/components-base';
import styled from 'styled-components';
import {
  ApiQueries,
  ExecuteContractModalProcess,
  ExecuteContractStore,
  NamingAssetStore,
  NamingJsonStore,
  RedeemAssetStore,
  SettingsStore,
  WalletStore,
  ZenJsUtils,
} from '@zen/common-app-parts';
import { assetUtils, numberUtils } from '@zen/common-utils';
import usePreventAction from '../../../../utils/usePreventAction';
import DestroyModalProcess from '../DestroyModalProcess';

/**
 * A form for issue and cancel
 * @param {Object} props
 * @param {string} props.asset
 * @param {boolean} props.isRedeem
 * @param {string} props.committedValue
 */
export default function RedeemButton({ asset, isRedeem, committedValue, ...props }) {
  const store = RedeemAssetStore.useStore();

  const { show, hide } = useModal();

  const { actions } = ExecuteContractStore.useStore();
  const {
    state: { balance },
    actions: { fetchBalance },
  } = WalletStore.useStore();
  const {
    state: {
      settings: { chain },
    },
  } = SettingsStore.useStore();
  const { state: namingContract } = NamingJsonStore.useStore();
  const { state: namingAsset } = NamingAssetStore.useStore();
  const { data: contracts } = ApiQueries.useFetchActiveContracts(10000);

  const setExecutionData = () => {
    fetchBalance().catch(() => {});
    actions.setAddressReadOnly(false);
    actions.reset();
    actions.setActiveContracts(contracts);
    actions.setAddress(ZenJsUtils.getContractAddressFromAsset(chain, asset));
    actions.setAllReadOnly(true);
    actions.setAsset({ index: 0, value: asset });
    actions.setAmount({ index: 0, value: balance[asset] });
    actions.setCommand('Redeem');
    actions.setIncludeSender(true);
  };

  const handleRedeem = async (e) => {
    e && e.preventDefault && e.preventDefault();
    store.actions
      .generate({ asset: asset, setMessage: actions.setMessageBody, chain })
      .then(() => {
        show(() => (
          <Modal>
            <ModalContent large>
              <ExecuteContractModalProcess
                contractId={ZenJsUtils.getContractIdFromAsset(asset).toHex()}
                onFinish={hide}
                usePreventAction={usePreventAction}
                naming={namingContract}
              />
            </ModalContent>
          </Modal>
        ));
      })
      .catch((error) =>
        show(() => (
          <Alert type={Alert.TYPE.ERROR} onDismiss={hide} title="Error">
            {error.message}
          </Alert>
        ))
      )
      .finally(setExecutionData());
  };

  function handleError(error) {
    show(() => (
      <Alert type={Alert.TYPE.ERROR} onDismiss={hide} title="Error">
        Couldn&apos;t destroy your token
        <br />
        Error message: {error.message}
      </Alert>
    ));
  }
  function handleSuccess() {
    const displayName =
      namingAsset && namingAsset[asset]
        ? namingAsset[asset].shortName
        : assetUtils.truncateOrNameAsset(asset);
    hide();
    notification(
      `You have just destroyed ${numberUtils.toDisplay(balance[asset])} ${displayName}`,
      {
        type: notification.TYPE.SUCCESS,
      }
    );
  }

  const handleDestroy = async (e) => {
    e && e.preventDefault && e.preventDefault();
    actions.runStarted();
    show(() => (
      <Modal onDismiss={hide}>
        <ModalContent onClose={hide}>
          <DestroyModalProcess
            onCancel={hide}
            onError={handleError}
            onSuccess={handleSuccess}
            spend={{ asset, amount: balance[asset] }}
            assetMetadata={namingAsset && namingAsset[asset]}
            committedValue={committedValue}
          />
        </ModalContent>
      </Modal>
    ));
  };

  const onClick = isRedeem ? handleRedeem : handleDestroy;

  return (
    <ActionButton onClick={onClick} disabled={store.state.progress} {...props}>
      Exercise
    </ActionButton>
  );
}
const ActionButton = styled(Button)`
  padding: ${tu.space('xxs')};
`;
